import { cloneDeep, get, union } from 'lodash';
import { TextAlignment } from '../../../../../../common/table/CellTypeConstants';
import { CellData, TableContentData } from '../../../../../../common/table/TableTypes';
import { getColumnCount, getRowCount } from '../../../../../../common/table/utils/tableCellDataPropertyUtils';
import { getCellRangeFromStartAndSize } from '../../selectionUtils/tableSelectionRangeUtils';
import { forEachCellInRange } from '../../selectionUtils/tableSelectionIterationUtils';

type ContinuedCellFormatting = {
    background?: string | null;
    textStyle?: string[];
    textAlignment?: TextAlignment;
};

type ContinueCellFormattingArgs = {
    dataArray: TableContentData;
    colIndex: number;
    nColsAdded: number;
    rowIndex: number;
    nRowsAdded: number;
};

type CellFormatNeighbourArgs = {
    row: number;
    col: number;
    dataArray: TableContentData;
    numberAdded: number;
    isAddingRows: boolean;
};

/**
 * Removes undefined and empty formatting values so copied formatting stays sparse.
 */
const normaliseCellFormat = (cellFormat: Partial<CellData>): Partial<CellData> => {
    const normalisedCellFormat = { ...cellFormat };

    if (normalisedCellFormat.background === undefined) delete normalisedCellFormat.background;
    if (normalisedCellFormat.textAlignment === undefined) delete normalisedCellFormat.textAlignment;
    if (!normalisedCellFormat.textStyle?.length) delete normalisedCellFormat.textStyle;

    return normalisedCellFormat;
};

/**
 * Returns the neighbour coordinates used to infer inserted row or column formatting.
 */
const getNeighbourCoords = ({ row, col, numberAdded, isAddingRows }: CellFormatNeighbourArgs) => ({
    twoBefore: isAddingRows ? { row: row - 2, col } : { row, col: col - 2 },
    oneBefore: isAddingRows ? { row: row - 1, col } : { row, col: col - 1 },
    oneAfter: isAddingRows ? { row: row + numberAdded, col } : { row, col: col + numberAdded },
    twoAfter: isAddingRows ? { row: row + numberAdded + 1, col } : { row, col: col + numberAdded + 1 },
});

/**
 * Returns the neighbour cells used to infer inserted row or column formatting.
 */
const getNeighbourCells = (args: CellFormatNeighbourArgs) => {
    const { dataArray } = args;
    const { twoBefore, oneBefore, oneAfter, twoAfter } = getNeighbourCoords(args);

    return {
        twoBeforeCell: get(dataArray, [twoBefore.row, twoBefore.col]),
        oneBeforeCell: get(dataArray, [oneBefore.row, oneBefore.col]),
        oneAfterCell: get(dataArray, [oneAfter.row, oneAfter.col]),
        twoAfterCell: get(dataArray, [twoAfter.row, twoAfter.col]),
    };
};

/**
 * Returns the previous row or column count before the insertion was applied.
 */
const getPreviousAxisCount = ({
    dataArray,
    numberAdded,
    isAddingRows,
}: Pick<CellFormatNeighbourArgs, 'dataArray' | 'numberAdded' | 'isAddingRows'>): number => {
    const rowCount = getRowCount(dataArray);
    const columnCount = getColumnCount(dataArray);

    return isAddingRows ? rowCount - numberAdded : columnCount - numberAdded;
};

/**
 * Extends formatting from the cells before the inserted range when the pattern is consistent.
 */
const applyBeforeCellFormatting = ({
    oneBeforeCell,
    twoBeforeCell,
    oneAfterCell,
    previousCount,
}: {
    oneBeforeCell?: CellData;
    twoBeforeCell?: CellData;
    oneAfterCell?: CellData;
    previousCount: number;
}): ContinuedCellFormatting => {
    if (!oneBeforeCell) return {};

    const {
        background: beforeBackground,
        textStyle: beforeTextStyle,
        textAlignment: beforeTextAlignment,
    } = oneBeforeCell;

    if (previousCount === 1) {
        return normaliseCellFormat({ background: beforeBackground, textStyle: beforeTextStyle });
    }

    return normaliseCellFormat({
        background:
            twoBeforeCell?.background === beforeBackground || oneAfterCell?.background === beforeBackground
                ? beforeBackground
                : undefined,
        textAlignment:
            twoBeforeCell?.textAlignment === beforeTextAlignment || oneAfterCell?.textAlignment === beforeTextAlignment
                ? beforeTextAlignment
                : undefined,
        textStyle: beforeTextStyle?.filter(
            (style) => twoBeforeCell?.textStyle?.includes(style) || oneAfterCell?.textStyle?.includes(style),
        ),
    });
};

/**
 * Extends formatting from the cells after the inserted range when the pattern is consistent.
 */
const applyAfterCellFormatting = ({
    existingFormatting,
    oneAfterCell,
    twoAfterCell,
    previousCount,
}: {
    existingFormatting: ContinuedCellFormatting;
    oneAfterCell?: CellData;
    twoAfterCell?: CellData;
    previousCount: number;
}): ContinuedCellFormatting => {
    if (!oneAfterCell) return existingFormatting;

    const { background: afterBackground, textStyle: afterTextStyle, textAlignment: afterTextAlignment } = oneAfterCell;

    if (previousCount === 1) {
        return normaliseCellFormat({ background: afterBackground, textStyle: afterTextStyle });
    }

    const textStylesFromCellsAfter = afterTextStyle?.filter((style) => twoAfterCell?.textStyle?.includes(style));

    return normaliseCellFormat({
        ...existingFormatting,
        background:
            existingFormatting.background || twoAfterCell?.background !== afterBackground
                ? existingFormatting.background
                : afterBackground,
        textAlignment:
            existingFormatting.textAlignment || twoAfterCell?.textAlignment !== afterTextAlignment
                ? existingFormatting.textAlignment
                : afterTextAlignment,
        textStyle: union(existingFormatting.textStyle, textStylesFromCellsAfter),
    });
};

/**
 * Infers the formatting to apply to a newly inserted row or column cell from its neighbours.
 */
const getCellFormatFromNeighbours = ({
    row,
    col,
    dataArray,
    numberAdded,
    isAddingRows,
}: CellFormatNeighbourArgs): Partial<CellData> => {
    const { twoBeforeCell, oneBeforeCell, oneAfterCell, twoAfterCell } = getNeighbourCells({
        row,
        col,
        dataArray,
        numberAdded,
        isAddingRows,
    });
    const previousCount = getPreviousAxisCount({ dataArray, numberAdded, isAddingRows });

    const beforeFormatting = applyBeforeCellFormatting({
        oneBeforeCell,
        twoBeforeCell,
        oneAfterCell,
        previousCount,
    });

    return applyAfterCellFormatting({
        existingFormatting: beforeFormatting,
        oneAfterCell,
        twoAfterCell,
        previousCount,
    });
};

/**
 * Applies inferred formatting across an inserted rectangular range.
 */
const applyFormattingContinuation = ({
    dataArray,
    cellRange,
    numberAdded,
    isAddingRows,
}: {
    dataArray: TableContentData;
    cellRange: ReturnType<typeof getCellRangeFromStartAndSize>;
    numberAdded: number;
    isAddingRows: boolean;
}): void => {
    forEachCellInRange({
        cellRange,
        callback: ({ row, col }) => {
            dataArray[row][col] = {
                ...getCellFormatFromNeighbours({ row, col, dataArray, numberAdded, isAddingRows }),
                ...dataArray[row][col],
            };
        },
    });
};

/**
 * Continues neighbouring cell formatting onto newly inserted rows or columns in serialized table data.
 */
export const continueCellFormatting = ({
    dataArray,
    colIndex,
    nColsAdded,
    rowIndex,
    nRowsAdded,
}: ContinueCellFormattingArgs): TableContentData => {
    const newData = cloneDeep(dataArray);
    const rowCount = getRowCount(newData);
    const columnCount = getColumnCount(newData);

    if (nColsAdded > 0) {
        applyFormattingContinuation({
            dataArray: newData,
            cellRange: getCellRangeFromStartAndSize({
                start: { row: 0, col: colIndex },
                size: { row: rowCount, col: nColsAdded },
            }),
            numberAdded: nColsAdded,
            isAddingRows: false,
        });
    }

    if (nRowsAdded > 0) {
        applyFormattingContinuation({
            dataArray: newData,
            cellRange: getCellRangeFromStartAndSize({
                start: { row: rowIndex, col: 0 },
                size: { row: nRowsAdded, col: columnCount },
            }),
            numberAdded: nRowsAdded,
            isAddingRows: true,
        });
    }

    return newData;
};
